import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Plus, VButton, VChip, VFlex, cn } from '@/package';
import { VStack } from '@/package';
import type { User, UserGroup } from '@/type';

import { TaskCreateFormUser } from './TaskCreateFormUser';
import type { TaskCreateFormType } from './type';

export const TaskWorker = ({
  projectUserList,
  readOnly,
  userGroups,
}: {
  projectUserList: User[];
  readOnly: boolean;
  userGroups: UserGroup[];
}) => {
  const [isUserAdding, setIsUserAdding] = useState(false);
  const {
    watch,
    register,
    getValues,
    formState: { touchedFields },
  } = useFormContext<TaskCreateFormType>();
  const worker = watch('worker');
  const { t } = useTranslation();

  const validateWorker = (value?: User[]) => {
    const currentWorker = value ?? getValues('worker');
    return Boolean(currentWorker?.length && currentWorker.length > 0);
  };

  register('worker', {
    validate: validateWorker,
  });

  return (
    <>
      <VStack
        gap={1}
        className={cn(
          'h-[174px] overflow-hidden rounded-md border bg-white p-2',
          {
            'border-error-500': !validateWorker() && touchedFields.worker,
            'border-gray-300': validateWorker() || !touchedFields.worker,
          },
        )}
      >
        <VStack gap={0.5}>
          <VFlex items="center" justify="between">
            <VFlex className="h-[18px]" gap={0.125} items="center">
              <span className="text-body-md text-gray-600">
                {t('project.labelMember.constructional')}
              </span>
              <VStack className="h-full">
                <div className="bg size-1 rounded-full bg-error-500" />
              </VStack>
            </VFlex>
            <span className="text-body-md text-gray-600">
              Total {worker?.length ?? 0}
            </span>
          </VFlex>

          {!readOnly && (
            <VButton
              fluid
              outlined
              size="sm"
              type="button"
              variant="secondary"
              onClick={() => setIsUserAdding(true)}
            >
              <Plus />
              <span className="-translate-y-px">
                {t('common.add.ametrometer')}
              </span>
            </VButton>
          )}
        </VStack>

        <TaskWorkerList readOnly={readOnly} />
      </VStack>

      {isUserAdding && (
        <TaskCreateFormUser
          isUserAdding={isUserAdding}
          setIsUserAdding={setIsUserAdding}
          userList={projectUserList}
          userGroups={userGroups}
        />
      )}
    </>
  );
};

const TaskWorkerList = ({ readOnly }: { readOnly: boolean }) => {
  const { watch, setValue } = useFormContext<TaskCreateFormType>();
  const worker = watch('worker');

  return (
    <VFlex className="flex-1 overflow-y-auto">
      <VFlex className="h-fit flex-wrap gap-1">
        {worker?.map(user => (
          <VChip
            key={user.id}
            onDelete={() => {
              setValue(
                'worker',
                worker?.filter(w => w.id !== user.id),
                {
                  shouldTouch: true,
                  shouldValidate: true,
                },
              );
            }}
            disabled={readOnly}
          >
            {user.email}
          </VChip>
        ))}
      </VFlex>
    </VFlex>
  );
};

TaskWorker.displayName = 'TaskWorker';
TaskWorkerList.displayName = 'TaskWorkerList';
